import React, { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import styles from './CreatePostModal.module.css'
import { AlertModal } from '../AlertModal'

const CreatePostModal = ({ isOpen, onClose, onSave, isEdit = false, initialData = null }) => {
  const [postData, setPostData] = useState({
    title: initialData?.title || '',
    content: initialData?.content || '',
    imageFile: null
  })
  const [previewImage, setPreviewImage] = useState(null)
  const fileInputRef = useRef(null)
  const [showAlert, setShowAlert] = useState(false)
  const [alertMessage, setAlertMessage] = useState('')

  useEffect(() => {
    if (isOpen) {
      if (isEdit && initialData) {
        setPostData({
          title: initialData.title || '',
          content: initialData.content || '',
          imageFile: null
        })
      } else if (!isEdit) {
        setPostData({ title: '', content: '', imageFile: null })
        setPreviewImage(null)
      }
    }
  }, [isOpen, isEdit, initialData?.title, initialData?.content])

  const handleImageSelect = (e) => {
    const file = e.target.files[0]
    if (file) {
      const reader = new FileReader()
      reader.onloadend = () => {
        setPreviewImage(reader.result)
      }
      reader.readAsDataURL(file)

      setPostData(prev => ({ ...prev, imageFile: file }))
    }
  }

  const handleRemoveImage = () => {
    setPreviewImage(null)
    setPostData({ ...postData, imageFile: null })
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleSave = async () => {
    if (!postData.title.trim()) {
      setAlertMessage('제목을 입력해주세요.')
      setShowAlert(true)
      return
    }
    if (!postData.content.trim()) {
      setAlertMessage('내용을 입력해주세요.')
      setShowAlert(true)
      return
    }

    try {
      await onSave(postData)   
      handleCancel()
    } catch (e) {
      console.error(e)
    }
  }

  const handleCancel = () => {
    setPostData({ title: '', content: '', imageFile: null })
    setPreviewImage(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
    onClose()
  }

  if (!isOpen) return null

  return createPortal(
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2>{isEdit ? '게시글 수정' : '게시글 작성'}</h2>
        </div>

        <div className={styles.modalBody}>
          {/* 이미지 업로드 섹션 - 수정 모드에서는 제외 */}
          {!isEdit && (
            <div className={styles.imageSection}>
              {previewImage ? (
                <div className={styles.imagePreview}>
                  <img src={previewImage} alt="Preview" />
                  <button 
                    className={styles.removeImageBtn}
                    onClick={handleRemoveImage}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              ) : (
                <button 
                  className={styles.uploadBtn}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <i className="fas fa-image"></i>
                  <span>사진 업로드 (선택)</span>
                </button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageSelect}
                style={{ display: 'none' }}
              />
            </div>
          )}

          {/* 제목 입력 */}
          <div className={styles.formGroup}>
            <label>제목</label>
            <input
              type="text"
              value={postData.title}
              onChange={(e) => setPostData({ ...postData, title: e.target.value })}
              placeholder="제목을 입력하세요"
              maxLength={100}
              className={styles.titleInput}
            />
            <span className={styles.charCount}>{postData.title.length}/100</span>
          </div>

          {/* 내용 입력 */}
          <div className={styles.formGroup}>
            <label>내용</label>
            <textarea
              value={postData.content}
              onChange={(e) => setPostData({ ...postData, content: e.target.value })}
              placeholder="내용을 입력하세요"
              maxLength={1000}
              className={styles.contentTextarea}
            />
            <span className={styles.charCount}>{postData.content.length}/1000</span>
          </div>
        </div>

        <div className={styles.modalFooter}>
          <button className={styles.cancelBtn} onClick={handleCancel}>
            취소
          </button>
          <button className={styles.confirmBtn} onClick={handleSave}>
            {isEdit ? '수정 완료' : '작성 완료'}
          </button>
        </div>
      </div>
      <AlertModal
        isOpen={showAlert}
        onClose={() => setShowAlert(false)}
        message={alertMessage}
      />
    </div>,
    document.body
  )
}

export default CreatePostModal
